"use client";

import type { ReactNode } from "react";
import type { Tier } from "@mw/shared/pricing";
import type { Locale } from "@mw/shared/i18n";

import { useCurrency } from "@/app/_providers/currency";
import { EventName } from "@mw/shared/analyticsEventNames";
import Tts20 from "@ui/icons/20x20/tts-20.svg";
import ArrowDownRight14 from "@ui/icons/14x14/arrow-down-right-14.svg";
import { formatNumberToKM, formatPrice } from "@utilities/numbers";
import { useLocale, useTranslations } from "next-intl";
import { getPricingCardContent, getPriceForCurrency, type PricingCardFeatureItem } from "@mw/shared/pricing";

import { DottedBorder } from "../core/GradientBorder";
import { LinkTracked } from "../core/LinkTracked";
import { getFormattedPrices, TIER_COVER_IMAGES } from "./utils";
import { getHostingCostPerMinute } from "./agents-pricing-data";

import { PricingCardHeader } from "./PricingCardHeader";
import { PricingCardLayout } from "./PricingCardLayout";

type BillingCycle = "monthly" | "yearly";
type PlatformVariant = "api" | "creative_platform" | "agents_platform" | "simple";
type PricingDisplay = "tiers" | "agent-calls";

interface PricingCardV3Props {
  tier: Tier;
  variant: PlatformVariant;
  billingCycle: BillingCycle;
  /** Controls what price to display: "tiers" shows tier price, "agent-calls" shows hosting cost per minute */
  pricingDisplay?: PricingDisplay;
  /** Whether to show the credits included section at the bottom. Defaults to true. */
  showCreditsIncluded?: boolean;
  /** Whether this card is highlighted (shows cover image and white text) */
  highlighted?: boolean;
  /** Callback when hover starts on this card */
  onHoverStart?: () => void;
}

export const PricingCardV3 = ({
  tier,
  variant,
  billingCycle,
  pricingDisplay = "tiers",
  showCreditsIncluded = true,
  highlighted = false,
  onHoverStart,
}: PricingCardV3Props) => {
  const t = useTranslations("PricingClientKeys.PricingCard");
  const locale = useLocale() as Locale;
  const { currency, currencySymbol } = useCurrency();

  const isEnterprise = tier.planId === "enterprise";
  const isPopular = tier.planId === "creator";

  const pricingCardContent = getPricingCardContent();
  const contentKey = tier.planId;
  const tierContent = pricingCardContent[contentKey as keyof typeof pricingCardContent];
  const platformContent =
    variant === "api"
      ? tierContent.api
      : variant === "creative_platform"
        ? tierContent.creative_platform
        : variant === "agents_platform"
          ? tierContent.agents_platform
          : tierContent.simple;

  const { price, discountedPrice, discountMessageKey, formattedOriginalPrice, currencyFormattedPrice } =
    getFormattedPrices(
      tier.pricing,
      billingCycle === "yearly" ? "yearly" : "monthly",
      currency,
      currencySymbol,
      locale,
    );

  // Get hosting cost per minute for agent-calls pricing display
  const hostingCostPerMinute = getHostingCostPerMinute(tier);
  const formattedHostingCost =
    hostingCostPerMinute !== undefined
      ? formatPrice(getPriceForCurrency(hostingCostPerMinute, currency), currencySymbol, locale)
      : null;

  const getCTALabel = () => {
    if (isEnterprise) return t("cta_label_enterprise");
    if (tier.planId === "free") return t("cta_label_free");
    return t("cta_label_choose", { tier: t(platformContent.title) });
  };

  const coverImage = TIER_COVER_IMAGES[tier.planId];

  // Determine price and price unit based on display mode
  const displayPrice = isEnterprise
    ? t("custom_pricing")
    : pricingDisplay === "agent-calls" && formattedHostingCost
      ? formattedHostingCost
      : currencyFormattedPrice;

  const displayPriceUnit = isEnterprise
    ? ""
    : pricingDisplay === "agent-calls"
      ? t("per_minute")
      : t("period_month_long");

  // Discount message for non-enterprise, non-agent-calls pricing
  const discountMessageContent =
    !isEnterprise &&
    pricingDisplay !== "agent-calls" &&
    discountedPrice &&
    price &&
    formattedOriginalPrice &&
    discountMessageKey ? (
      <p className='tw-opacity-80 tw-mb-2 tw-type-xs'>
        <span className='tw-line-through'>{formattedOriginalPrice}</span> <span>{t(discountMessageKey)}</span>
      </p>
    ) : undefined;

  // Popular badge subtitle - white when highlighted, dark when not
  const badgeColorClass = highlighted ? "tw-border-white tw-text-white" : "tw-border-current tw-text-current";
  const subtitleContent = isPopular ? (
    <div
      className={`tw-inline-block tw-border tw-px-3 tw-py-1 tw-rounded-lg tw-font-medium tw-transition-colors tw-type-xs ${badgeColorClass}`}
    >
      {t("popular")}
    </div>
  ) : undefined;

  return (
    <PricingCardLayout
      onHoverStart={onHoverStart}
      header={
        <PricingCardHeader
          title={t(platformContent.title)}
          subtitle={subtitleContent}
          price={displayPrice}
          priceUnit={displayPriceUnit}
          discountMessage={discountMessageContent}
          coverImage={coverImage}
          backgroundColor='dark'
          highlighted={highlighted}
        />
      }
      cta={
        <LinkTracked
          eventName={EventName.mw_pricing_card_cta_clicked}
          eventProperties={{
            tier: tier.planId,
            variant: variant,
          }}
          asButton
          buttonVariants={{
            variant: "primary",
            size: "lg",
            expand: true,
          }}
          href={tier.cta.href}
          label={getCTALabel()}
        />
      }
      ctaClassName='tw-mt-4 tw-mb-12'
      features={
        <>
          {platformContent.features_summary_label && tier.planId !== "free" && (
            <>
              <div className='tw-flex tw-items-center tw-gap-2 tw-py-3'>
                <span className='tw-text-gray-600 tw-type-sm'>{t(platformContent.features_summary_label)}</span>
                <ArrowDownRight14 className='tw-flex-shrink-0 tw-text-gray-600' />
              </div>
              <DottedBorder side='inline' />
            </>
          )}

          <ul>
            {(platformContent.features as PricingCardFeatureItem[]).map((feature, index) => (
              <li key={index}>
                <div className='tw-flex tw-items-start tw-gap-3 tw-py-3'>
                  <Tts20 className='tw-flex-shrink-0 tw-text-current' />
                  {}
                  <span className='tw-type-sm'>
                    {t.rich(feature.feature_name_translation_key as any, {
                      ...(feature.params?.(tier) as any),
                      strong: (chunks: ReactNode) => <strong className='tw-font-medium'>{chunks}</strong>,
                    })}
                  </span>
                </div>
                {index < platformContent.features.length - 1 && <DottedBorder side='inline' />}
              </li>
            ))}
          </ul>
        </>
      }
      footer={
        showCreditsIncluded ? (
          <div className='tw-mt-12'>
            {isEnterprise ? (
              <p className='tw-font-normal tw-type-base'>{t("custom_number_of_credits")}</p>
            ) : (
              <p className='tw-font-normal tw-type-base tw-flex tw-mb-1 tw-items-end'>
                <span className='tw-text-black tw-type-xl'>
                  {t("credits_amount", { credit_limit: formatNumberToKM(tier.pricing.includedCredits) })}
                </span>
                <span className='tw-opacity-60 tw-type-xs tw-ml-1'> {t("period_month_long")}</span>
              </p>
            )}
            {tier.capabilities.seats && (
              <p className='tw-text-gray-600 tw-type-sm'>{t("seats", { seats: tier.capabilities.seats })}</p>
            )}
          </div>
        ) : undefined
      }
    />
  );
};
